class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    PredictiveSearch.instanceCount = (PredictiveSearch.instanceCount || 0) + 1;
    this.instanceId = `predictive-search-instance-${PredictiveSearch.instanceCount}`;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    const firstMatchIndex = currentButtonText.indexOf(previousTerm);
    if (firstMatchIndex === -1) return;

    const secondMatchIndex = currentButtonText.indexOf(previousTerm, firstMatchIndex + previousTerm.length);
    if (secondMatchIndex !== -1) return;

    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getNormalizedPartNumberQuery(searchTerm) {
    if (!this.isPartNumberLikeQuery(searchTerm) || !/[\s._/\\-]/.test(searchTerm)) return '';

    return this.getNormalizedSkuQuery(searchTerm);
  }

  getNormalizedSkuQuery(searchTerm) {
    return searchTerm.trim().replace(/[\s._/\\-]+/g, '');
  }

  isPartNumberLikeQuery(searchTerm) {
    const normalizedQuery = this.getNormalizedSkuQuery(searchTerm);
    if (normalizedQuery.length < 3 || normalizedQuery.length > 64) return false;
    if (!/\d/.test(normalizedQuery) || !/^[a-z0-9]+$/i.test(normalizedQuery)) return false;

    const queryParts = searchTerm.split(/[\s._/\\-]+/).filter(Boolean);
    return !queryParts.some((part) => /^[a-z]{3,}$/i.test(part));
  }

  getExactLookupUrl(searchTerm) {
    const requestParameters = new URLSearchParams({
      q: this.getNormalizedSkuQuery(searchTerm),
      section_id: 'tccs-exact-search',
      'options[prefix]': 'none',
    });

    return `${routes.search_url}?${requestParameters.toString()}`;
  }

  getRequestUrl(searchTerm) {
    const requestParameters = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][prefix]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
    });

    return `${routes.predictive_search_url}?${requestParameters.toString()}`;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    this.abortController.abort();
    this.abortController = new AbortController();

    const requestSpecs = [{ term: searchTerm, type: 'predictive' }];
    const normalizedPartNumberQuery = this.getNormalizedPartNumberQuery(searchTerm);
    if (normalizedPartNumberQuery && normalizedPartNumberQuery.toLowerCase() !== searchTerm.toLowerCase()) {
      requestSpecs.push({ term: normalizedPartNumberQuery, type: 'predictive' });
    }
    if (this.isPartNumberLikeQuery(searchTerm)) {
      requestSpecs.push({ term: searchTerm, type: 'exact' });
    }

    const requests = requestSpecs.map((requestSpec) =>
      fetch(
        requestSpec.type === 'exact' ? this.getExactLookupUrl(requestSpec.term) : this.getRequestUrl(requestSpec.term),
        { signal: this.abortController.signal }
      ).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
    );

    Promise.allSettled(requests)
      .then((requestResults) => {
        const parsedResults = requestResults.flatMap((requestResult, index) => {
          if (requestResult.status !== 'fulfilled') return [];

          const element = new DOMParser()
            .parseFromString(requestResult.value, 'text/html')
            .querySelector(
              requestSpecs[index].type === 'exact'
                ? '#shopify-section-tccs-exact-search'
                : '#shopify-section-predictive-search'
            );

          return element ? [{ element, requestSpec: requestSpecs[index] }] : [];
        });

        if (!parsedResults.length) {
          const rejectedRequest = requestResults.find((requestResult) => requestResult.status === 'rejected');
          throw rejectedRequest?.reason || new Error('Predictive search request failed');
        }

        const primaryResult =
          parsedResults.find(
            (result) => result.requestSpec.type === 'predictive' && result.requestSpec.term === searchTerm
          ) || parsedResults.find((result) => result.requestSpec.type === 'predictive');
        if (!primaryResult) throw new Error('Predictive search request failed');
        this.mergeExactProductResults(
          primaryResult.element,
          parsedResults.map((result) => result.element)
        );
        const resultsMarkup = primaryResult.element.innerHTML;

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  mergeExactProductResults(primaryResult, resultElements) {
    const sourceSections = resultElements
      .map((result) => result.querySelector('.tccs-predictive-group--exact'))
      .filter(Boolean);
    if (!sourceSections.length) return;

    const wrapper = primaryResult.querySelector('.predictive-search__results-groups-wrapper');
    if (!wrapper) return;

    let productGroup = primaryResult.querySelector('.predictive-search__result-group--products');
    let productsContainer = primaryResult.querySelector('.tccs-predictive-products');

    if (!productsContainer) {
      const sourceContainer = sourceSections[0].closest('.tccs-predictive-products');
      if (!sourceContainer) return;

      if (!productGroup) {
        productGroup = document.createElement('div');
        productGroup.className = 'predictive-search__result-group predictive-search__result-group--products';
        wrapper.prepend(productGroup);
      }

      productsContainer = sourceContainer.cloneNode(true);
      productGroup.prepend(productsContainer);
    }

    let exactSection = productsContainer.querySelector('.tccs-predictive-group--exact');
    if (!exactSection) {
      exactSection = sourceSections[0].cloneNode(true);
      const relatedSection = productsContainer.querySelector('.tccs-predictive-group:not(.tccs-predictive-group--exact)');
      productsContainer.insertBefore(exactSection, relatedSection || productsContainer.firstChild);
    }

    const exactList = exactSection.querySelector('.predictive-search__results-list');
    if (!exactList) return;

    const exactProductIds = new Set(
      Array.from(exactList.querySelectorAll('[data-product-id]')).map((item) => item.dataset.productId)
    );

    sourceSections.forEach((sourceSection) => {
      sourceSection.querySelectorAll('[data-product-id]').forEach((item) => {
        if (exactProductIds.has(item.dataset.productId)) return;
        exactProductIds.add(item.dataset.productId);
        exactList.append(item.cloneNode(true));
      });
    });

    exactList.querySelectorAll('[data-product-id]').forEach((item, index) => {
      item.id = `predictive-search-option-product-exact-${index + 1}`;
    });

    const relatedSection = productsContainer.querySelector('.tccs-predictive-group:not(.tccs-predictive-group--exact)');
    if (relatedSection) {
      relatedSection.querySelectorAll('[data-product-id]').forEach((item) => {
        if (exactProductIds.has(item.dataset.productId)) item.remove();
      });
      if (!relatedSection.querySelector('[data-product-id]')) relatedSection.remove();
    }

    wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');
    primaryResult.querySelector('.tccs-predictive-empty')?.remove();
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.moveProductResultsFirst();
    this.scopeResultIds();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  moveProductResultsFirst() {
    const wrapper = this.predictiveSearchResults.querySelector('.predictive-search__results-groups-wrapper');
    const productGroup = wrapper?.querySelector('.predictive-search__result-group--products');
    if (wrapper && productGroup && wrapper.firstElementChild !== productGroup) wrapper.prepend(productGroup);
  }

  scopeResultIds() {
    const idMap = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      const scopedId = `${this.instanceId}-${originalId}`;
      idMap.set(originalId, scopedId);
      element.id = scopedId;
    });

    const referenceAttributes = ['aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns', 'for'];
    [this.input, ...this.predictiveSearchResults.querySelectorAll('*')].forEach((element) => {
      referenceAttributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;

        const scopedValue = value
          .split(/\s+/)
          .map((id) => idMap.get(id) || id)
          .join(' ');
        element.setAttribute(attribute, scopedValue);
      });
    });

    const resultsListbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (resultsListbox) {
      this.input.setAttribute('aria-controls', resultsListbox.id);
      this.input.setAttribute('aria-owns', resultsListbox.id);
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
